import React, { useState, useEffect, useContext, useRef } from 'react';
import { getFirestore, doc, getDoc, updateDoc, onSnapshot } from 'firebase/firestore';
import { getAuth, signInAnonymously } from 'firebase/auth';
import { initializeApp } from 'firebase/app';
import { QuizContext } from 'modules/assessment/MasterQuizApp';
import { getCurrentUserId, getCurrentUserProfile, getCurrentUserRole } from 'shared/utils/SessionDataUtils';
import { Assessment } from 'modules/assessment/Assessment';
import apiCall from 'shared/services/ApiAdapter';
import { useLoading } from 'shared/contexts/Loading';

// ---------------------------------------------
// ## 🧑‍🎓 Student Components
// ---------------------------------------------

// Default placeholder code for students who haven't entered a code yet.
const DEFAULT_STUDENT_LISTEN_CODE = "00000000";

// --- UTILITIES ---

// Helper function to defensively convert Firestore object-arrays back to real arrays
const getQuestionsArray = (questions) => {
    return Array.isArray(questions)
        ? questions
        : Object.values(questions || {});
};

// Helper function to get the correct answer text for a question
const getCorrectAnswerText = (q) => {
    const correctChoice = q.answerChoices?.find(c => c.isCorrect);
    return correctChoice ? correctChoice.choiceText : null;
};

// Helper function to combine question text and details
const getFullQuestionText = (q) => {
    return q.questionText + (q.details?.length > 0
        ? '\n\n' + q.details.map(d => d.questionChoice).join('\n')
        : '');
};

// Helper function to get options array
const getOptionsArray = (q) => {
    return q.answerChoices?.map(c => c.choiceText) || [];
};

// Helper to merge cached content with live mutable state
// liveQuestionStates is the small array of mutable objects (id, active, answeredBy)
const mergeQuestionState = (liveQuestionStates, cachedQs) => {
    // If cache is not ready, we can't merge meaningfully.
    if (cachedQs.length === 0) return [];

    const liveMutableQs = getQuestionsArray(liveQuestionStates);

    // Merge cached immutable content with live mutable data
    return cachedQs.map(cachedQ => {
        // Find the corresponding mutable state
        const liveQ = liveMutableQs.find(lq => lq.id.toString() === cachedQ.id.toString());
        return {
            ...cachedQ, // Full content (Local Source)
            // Add/Override mutable state (Live Source)
            answeredBy: liveQ?.answeredBy || {},
            active: liveQ?.active || false,
        };
    });
};


// --- MAIN APP COMPONENT (StudentsApp) ---
const StudentQuizApp = ({ displayToast }) => {
    const { startLoading, stopLoading } = useLoading();

    const [studentInfo, setStudentInfo] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [userId, setUserId] = useState(null);
    const [quizCode, setQuizCode] = useState(DEFAULT_STUDENT_LISTEN_CODE);
    const [liveState, setLiveState] = useState(null);
    // State to hold immutable question content
    const [cachedQuestions, setCachedQuestions] = useState([]);

    const userRole = getCurrentUserRole();

    const firebaseConfigRef = useRef({});
    const dbRef = useRef(null);
    const authRef = useRef(null);
    const quizDocRef = useRef(null);
    const unsubscribeRef = useRef(null);

    const setupQuizListener = (db, currentQuizCode) => {
        if (unsubscribeRef.current) {
            unsubscribeRef.current();
        }

        // NOTE: firebaseConfigRef.current must have been populated by initFirebase before this runs
        if (!firebaseConfigRef.current.appId) {
            console.error("Firebase App ID missing, cannot set up quiz listener.");
            return;
        }

        const docPath = `/artifacts/${firebaseConfigRef.current.appId}/public/data/quiz/${currentQuizCode}`;
        quizDocRef.current = doc(db, docPath);

        setIsLoading(true);
        setLiveState(null);
        // Reset cache only if the code changes, or rely on the initial check

        const unsubscribe = onSnapshot(quizDocRef.current, (docSnapshot) => {
            if (docSnapshot.exists()) {
                const data = docSnapshot.data();

                // Read mutable states from the dedicated field
                const liveQuestionStates = getQuestionsArray(data.questionStates);

                // --- 1. Initial Load and Caching (Only run if cache is empty) ---
                // We use data.questionContent which should be present when status is 'lobby'
                const fullContentFromDoc = data.questionContent;

                if (cachedQuestions.length === 0 && fullContentFromDoc && fullContentFromDoc.length > 0) {

                    // Cache the content field exactly as it exists in the document
                    setCachedQuestions(fullContentFromDoc);
                    console.log(`Questions content cached successfully from document's questionContent field.`);
                }

                // --- 2. Update liveState with MUTABLE fields only (read from questionStates) ---
                const mutableQuestionState = liveQuestionStates.map(q => ({
                    id: q.id,
                    answeredBy: q.answeredBy,
                    active: q.active
                }));

                const mutableLiveState = {
                    quiz: data.quiz,
                    joinedStudents: data.joinedStudents,
                    activeQuestionIndex: data.activeQuestionIndex,
                    // 🆕 Store the mutable subset under the new name
                    questionStates: mutableQuestionState,
                };

                setLiveState(mutableLiveState);

            } else {
                setLiveState(null);
                setCachedQuestions([]);
                console.warn(`Quiz document for code ${currentQuizCode} not found.`);
            }
            setIsLoading(false);
        }, (error) => {
            console.error("Error listening to quiz state:", error);
            setIsLoading(false);
        });

        unsubscribeRef.current = unsubscribe;
        setQuizCode(currentQuizCode);
    };


    useEffect(() => {
        const initFirebase = async () => {
            try {
                // Start loading
                startLoading();

                // 1. FETCH FIREBASE CONFIG (ASYNC)
                const url = `/config/firebase`;
                const data = await apiCall(
                    url,
                    null,
                    "GET",
                    null,
                    startLoading,
                    stopLoading,
                    displayToast
                );

                // 2. SET CONFIG REFERENCE
                const config = data;
                firebaseConfigRef.current = config;

                // 3. INITIALIZE FIREBASE APP *AFTER* CONFIG IS FETCHED
                const app = initializeApp(firebaseConfigRef.current);
                const db = getFirestore(app);
                const auth = getAuth(app);
                dbRef.current = db;
                authRef.current = auth;

                // 4. AUTHENTICATE USER
                await signInAnonymously(auth);
                const currentUserId = getCurrentUserId()

                setUserId(currentUserId);

                const { firstName, lastName } = getCurrentUserProfile()
                const assignedInfo = {
                    id: `${firstName?.toLowerCase()}_${lastName?.toLowerCase()}`,
                    name: `${firstName} ${lastName}`
                };

                setStudentInfo(assignedInfo);

                // 5. SETUP INITIAL LISTENER *AFTER* ALL FIREBASE TOOLS ARE READY
                setupQuizListener(db, DEFAULT_STUDENT_LISTEN_CODE);

            } catch (error) {
                console.error("Failed to initialize Firebase or authenticate:", error);
                // Display error toast if initialization fails completely
                displayToast('Firebase initialization failed. Please try refreshing.', 'error');
            } finally {
                // Stop loading regardless of success/failure
                stopLoading();
                setIsLoading(false);
            }
        };

        if (!authRef.current) {
            initFirebase();
        }

        return () => {
            if (unsubscribeRef.current) {
                unsubscribeRef.current();
            }
        };
    }, []); // Empty dependency array means this runs only on mount

    const updateQuizState = async (updates) => {
        if (quizCode === DEFAULT_STUDENT_LISTEN_CODE || !quizDocRef.current) {
            console.error("Firestore document reference is not available. Have you joined a quiz?");
            return;
        }
        try {
            // NOTE: Student updates only target mutable fields like 'questionStates' and 'joinedStudents'
            await updateDoc(quizDocRef.current, updates);
        } catch (error) {
            console.error("Failed to update quiz state in Firestore:", error);
        }
    };

    const renderView = () => {
        if (isLoading && !dbRef.current) {
            return (
                <div className="flex justify-center items-center h-64">
                    <svg className="animate-spin h-12 w-12 text-indigo-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                </div>
            );
        }

        // Render StudentContainer once Firebase is initialized (isLoading is false or dbRef is set)
        return (
            <StudentContainer
                studentInfo={studentInfo}
                currentQuizCode={quizCode}
                setupQuizListener={setupQuizListener}
                firebaseConfigRef={firebaseConfigRef}
            />
        );
    };

    return (
        // Pass cachedQuestions via context
        <QuizContext.Provider value={{ liveState, updateQuizState, userId, dbRef, cachedQuestions }}>
            <div className="text-gray-800 min-h-screen font-sans flex flex-col items-center p-4">
                <div className="w-full  p-6 md:p-10">
                    {renderView()}
                </div>
                {/* <div className="mt-4 p-2 text-xs text-gray-400">
                    Role: **{userRole.toUpperCase()}** | User ID: {userId || 'Authenticating...'} | Assigned Name: {studentInfo?.name} (ID: {studentInfo?.id})
                </div> */}
            </div>
        </QuizContext.Provider>
    );
};
export default StudentQuizApp;

// ... (StudentContainer, LiveQuizStudentView, StudentResultsScreen components remain the same)

const StudentContainer = ({ studentInfo, currentQuizCode, setupQuizListener, firebaseConfigRef }) => {
    const { liveState, updateQuizState, dbRef, cachedQuestions } = useContext(QuizContext);
    const [joinCodeInput, setJoinCodeInput] = useState('');
    const [hasJoined, setHasJoined] = useState(false);
    const [error, setError] = useState('');

    // ✅ State to hold the final, merged array of questions
    const [fullQuestionState, setFullQuestionState] = useState([]);

    const db = dbRef.current;

    // 1. EFFECT: Merge questions whenever liveState (mutable data) or cachedQuestions (content) updates
    useEffect(() => {
        if (liveState) {
            // 🆕 Reference the new liveState.questionStates field
            const mergedQuestions = mergeQuestionState(liveState.questionStates, cachedQuestions);
            setFullQuestionState(mergedQuestions);
        } else {
            setFullQuestionState([]);
        }
    }, [liveState, cachedQuestions]);

    // 2. Check joined status (as before)
    useEffect(() => {
        if (liveState && liveState.joinedStudents) {
            if (liveState.joinedStudents.some(s => s.id === studentInfo?.id)) {
                setHasJoined(true);
            } else {
                setHasJoined(false);
            }
        } else if (currentQuizCode !== DEFAULT_STUDENT_LISTEN_CODE) {
            setHasJoined(false);
        }
    }, [liveState, studentInfo?.id, currentQuizCode]);


    const handleJoinClick = async () => {
        setError('');

        const normalizedInputCode = joinCodeInput.toUpperCase().replace(/[^A-Z0-9]/g, '');

        if (normalizedInputCode.length !== 8) {
            setError('Please enter the complete 8-character code.');
            return;
        }

        // 1. Logic for joining a **NEW** quiz code (not the one we are currently listening to)
        if (normalizedInputCode !== currentQuizCode) {
            try {
                if (!db) {
                    setError('Database connection not established. Please wait.');
                    return;
                }
                const quizDocRefToJoin = doc(db, `/artifacts/${firebaseConfigRef.current.appId}/public/data/quiz/${normalizedInputCode}`);
                const quizDoc = await getDoc(quizDocRefToJoin);

                if (!quizDoc.exists()) {
                    setError('Invalid quiz code. Please confirm the code with your tutor.');
                    return;
                }

                const quizData = quizDoc.data();

                if (quizData.quiz.status !== 'lobby') {
                    setError('The quiz you tried to join has already started or finished.');
                    return;
                }

                // Setup the listener for the new valid code. The listener will cache content.
                setupQuizListener(db, normalizedInputCode);

                // **CRITICAL FIX: Write the join action to the new document**
                const studentToJoin = { id: studentInfo.id, name: studentInfo.name };
                const currentJoined = quizData.joinedStudents || [];

                // Only update if the student is not already listed in the quizData fetched during validation
                if (!currentJoined.some(s => s.id === studentInfo.id)) {
                    const newJoined = [...currentJoined, studentToJoin];

                    // Use updateDoc on the newly referenced document
                    await updateDoc(quizDocRefToJoin, { joinedStudents: newJoined });
                }
                // The new listener will automatically fetch the updated state, setting liveState.

                setJoinCodeInput('');
                return;

            } catch (e) {
                console.error("Error checking external quiz code or joining:", e);
                setError('Network error while checking the code or joining. Please try again.');
                return;
            }
        }

        // 2. Logic for joining the **CURRENTLY LISTENED** quiz 

        if (!liveState) {
            setError('Connecting to quiz document. Please wait a moment.');
            return;
        }

        if (liveState.quiz.status !== 'lobby') {
            setError('The quiz has already started or finished. Joining is closed.');
            return;
        }

        if (hasJoined) {
            return;
        }

        try {
            const studentToJoin = { id: studentInfo.id, name: studentInfo.name };

            const currentJoined = liveState.joinedStudents || [];

            // Check for duplicates before committing join to the current document
            if (currentJoined.some(s => s.id === studentInfo.id)) {
                setHasJoined(true);
                return;
            }

            const newJoined = [...currentJoined, studentToJoin];
            await updateQuizState({ joinedStudents: newJoined }); // This calls updateDoc on the current doc
            setHasJoined(true);
        } catch (error) {
            console.error("Error committing join:", error);
            setError('Failed to join quiz. Please try again.');
        }
    };


    const shouldShowJoinUI = currentQuizCode === DEFAULT_STUDENT_LISTEN_CODE || !liveState || liveState.quiz.status === 'lobby';

    if (shouldShowJoinUI) {
        // ... (Lobby/Join UI rendering logic remains the same)
        const normalizedInputLength = joinCodeInput.toUpperCase().replace(/[^A-Z0-9]/g, '').length;

        const isDefaultListenCode = currentQuizCode === DEFAULT_STUDENT_LISTEN_CODE;
        let joinCodeDisplay = isDefaultListenCode ? "ABCD-EFGH" : currentQuizCode.slice(0, 4) + '-' + currentQuizCode.slice(4);

        const buttonDisabled = normalizedInputLength !== 8;

        let buttonText = 'Join Quiz';
        if (liveState && liveState.quiz.status === 'finished') {
            buttonText = 'Quiz Finished';
        } else if (hasJoined && liveState && liveState.quiz.status === 'lobby') {
            buttonText = 'Waiting for quiz to start';
        } else if (normalizedInputLength === 8) {
            buttonText = 'Check Code & Join';
        }


        return (
            <div className="flex flex-col w-full min-h-screen items-center text-center space-y-6 animate-fade-in">
                <h1 className="text-3xl font-bold text-indigo-600 dark:text-indigo-400">Welcome, {studentInfo?.name}!</h1>
                <p className="text-gray-500 dark:text-gray-400">
                    {isDefaultListenCode || !liveState
                        ? "Enter the 4-4 Alphanumeric Code provided by your tutor to join."
                        : `You are in the lobby for quiz: **${joinCodeDisplay}**. Waiting for the tutor to start.`}
                </p>

                {((!hasJoined && !liveState) || isDefaultListenCode) && ( // Show input if not joined OR default code
                    <div className="w-full max-w-sm space-y-4">
                        <input
                            type="text"
                            value={joinCodeInput}
                            onChange={(e) => {
                                const val = e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '');
                                if (val.length > 4) {
                                    setJoinCodeInput(val.slice(0, 4) + '-' + val.slice(4, 8));
                                } else {
                                    setJoinCodeInput(val);
                                }
                            }}
                            placeholder={joinCodeDisplay}
                            maxLength={9}
                            className="w-full p-4 rounded-lg bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-white text-center text-2xl font-mono tracking-widest focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:focus:ring-indigo-400" />
                    </div>
                )}


                {error && <p className="text-rose-600 dark:text-rose-400 font-bold">{error}</p>}

                {isDefaultListenCode && !buttonDisabled && <p className="text-gray-500 dark:text-gray-400">Checking code: **{joinCodeInput.toUpperCase().replace(/[^A-Z0-9]/g, '')}**</p>}


                <button
                    onClick={handleJoinClick}
                    disabled={(liveState && liveState.quiz.status === 'finished') || (!hasJoined && buttonDisabled)}
                    className={`px-8 py-4 text-xl font-bold rounded-full transition-all duration-300 ${((liveState && liveState.quiz.status === 'finished') || (!hasJoined && buttonDisabled))
                        ? 'bg-gray-300 dark:bg-gray-700 text-gray-500 dark:text-gray-400 cursor-not-allowed'
                        : 'bg-indigo-600 dark:bg-indigo-700 text-white shadow-lg hover:bg-indigo-500 dark:hover:bg-indigo-600 hover:scale-105'}`}
                >
                    {buttonText}
                </button>
                {liveState && liveState.quiz.status !== 'lobby' && <p className="text-rose-600 dark:text-rose-400 font-bold mt-4">Joining is closed. Quiz Status: **{liveState.quiz.status.toUpperCase()}**</p>}
            </div>
        );
    }

    // --- Render Active Quiz Screen (if shouldShowJoinUI is false) ---
    // Use cachedQuestions array length as the definitive content size
    if (cachedQuestions.length === 0) {
        // Fallback loading while cache fills up after state transitions to 'active'
        return (
            <div className="text-center p-8 text-indigo-600 font-bold">Fetching question content...</div>
        );
    }

    // Use the definitive fullQuestionState derived synchronously
    const { quiz, activeQuestionIndex } = liveState;

    const currentQuestion = fullQuestionState[activeQuestionIndex];

    if (!currentQuestion) {
        return (
            <div className="text-center p-8 text-rose-600 font-bold">Error: Could not load active question data.</div>
        );
    }

    const handleAnswer = async (answer) => {
        if (quiz.status !== 'active' || currentQuestion.answeredBy[studentInfo.id]) {
            return;
        }

        const newAnsweredBy = { ...currentQuestion.answeredBy, [studentInfo.id]: answer };

        // Find the index of the question in the live mutable array
        // We look at the MINIMAL liveState.questionStates to find the index to update in Firestore
        // 🆕 Reference liveState.questionStates
        const questionsArray = getQuestionsArray(liveState.questionStates);
        const questionIndexInLiveState = questionsArray.findIndex(q => q.id.toString() === currentQuestion.id.toString());

        if (questionIndexInLiveState === -1) return;

        // 1. Create a new mutable questions array with the updated answeredBy field
        const newQuestions = questionsArray.map((q, index) => {
            if (index === questionIndexInLiveState) {
                // Ensure we only update mutable fields for Firestore write efficiency
                return { id: q.id, answeredBy: newAnsweredBy, active: q.active };
            }
            return q;
        });

        // 2. Write the entire mutable array back to Firestore, targeting the 'questionStates' field
        const updates = { questionStates: newQuestions };

        try {
            await updateQuizState(updates);
        } catch (error) {
            console.error("Error submitting answer:", error);
        }
    };

    const renderScreen = () => {
        if (quiz.status === 'finished') {
            return <StudentResultsScreen fullQuestionState={fullQuestionState} liveState={liveState} studentId={studentInfo.id} />;
        }

        if (quiz.status === 'active' && activeQuestionIndex !== -1) {
            return <LiveQuizStudentView fullQuestionState={fullQuestionState} liveState={liveState} student={studentInfo} onAnswer={handleAnswer} />;
        }

        return (
            <div className="text-center p-8 animate-fade-in">
                <h1 className="text-3xl font-bold text-gray-600">Quiz Inactive</h1>
                <p className="mt-4 text-lg text-gray-500">Welcome, <span className="font-bold text-indigo-600">{studentInfo?.name}</span>! Waiting for the tutor to activate the next step...</p>
                <div className="mt-8 flex justify-center">
                    <svg className="animate-spin h-12 w-12 text-indigo-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                </div>
            </div>
        );
    };

    return (
        <div className="flex flex-col  w-full items-center space-y-8">
            {renderScreen()}
        </div>
    );
};
// Inside your StudentApp file or a new component file
export const LiveQuizStudentView = ({
    fullQuestionState, // The merged array of cached content + live states
    liveState,
    studentInfo,
    onAnswer
}) => {
    // Timer duration in seconds (1 minute = 60 seconds)
    const TIMER_DURATION_SECONDS = 180;

    const { activeQuestionIndex, quiz } = liveState;

    // 1. Find the currently active question
    const activeQuestion = fullQuestionState[activeQuestionIndex];

    // Determine initial answered state for controls
    const isAnsweredByStudent = !!activeQuestion?.answeredBy[studentInfo?.id];

    // --- State for Timer Management ---
    const [timeLeft, setTimeLeft] = useState(TIMER_DURATION_SECONDS);

    // --- Effect to manage the countdown and reset ---
    useEffect(() => {
        // Reset timer when question ID changes or status goes active
        setTimeLeft(TIMER_DURATION_SECONDS);

        // Timer should only run if the quiz is active AND the student hasn't answered
        if (quiz.status !== 'active' || isAnsweredByStudent) {
            return;
        }

        const timerInterval = setInterval(() => {
            setTimeLeft((prevTime) => {
                if (prevTime <= 1) {
                    clearInterval(timerInterval);
                    // Optionally, trigger a submit with a null answer if time runs out
                    // onAnswer(null); 
                    return 0;
                }
                return prevTime - 1;
            });
        }, 1000);

        return () => clearInterval(timerInterval);
    }, [activeQuestion?.id, quiz.status, isAnsweredByStudent]); // Depend on question ID and answer status

    if (!activeQuestion || !activeQuestion.active || quiz.status !== 'active') {
        return <p className="text-center p-8 text-gray-500">Waiting for the next question to be activated...</p>;
    }


    // 2. Format the data for the Assessment component (single question array)
    const currentQuestionData = [{
        ...activeQuestion,
        id: activeQuestion.id,
        answer: activeQuestion.answeredBy[studentInfo?.id] || null,
        isAnswered: isAnsweredByStudent,
    }];

    // 3. Define the custom submission handler for a single answer
    const handleSingleAnswerSubmit = (results) => {
        const submittedAnswer = results[0]?.answer;
        if (submittedAnswer) {
            onAnswer(submittedAnswer);
        }
    };

    // Determine if input should be disabled due to answer or timeout
    const inputIsDisabled = isAnsweredByStudent || timeLeft === 0;

    return (
        <div className="flex flex-col w-full space-y-6 animate-fade-in">
            <h2 className="text-2xl font-bold text-gray-700 text-center">
                Live Question {activeQuestionIndex + 1}
            </h2>

            <div className="flex flex-col lg:flex-row gap-4 w-full">

                {/* Left Column (75% - Question Display via Assessment) */}
                <div className="w-full lg:w-3/4">
                    <Assessment
                        questionsData={currentQuestionData}
                        readOnly={inputIsDisabled}
                        timerDuration={TIMER_DURATION_SECONDS / 60}
                        enableTimer={true}
                        isLiveQuiz={true}
                        key={`q-${activeQuestion.id}`}
                        onExit={() => { }}
                        onAssessmentSubmit={handleSingleAnswerSubmit}
                        isSubmitting={false}
                        submitError={null}
                        course={liveState.quiz.name}
                        evaluation={null}
                    />
                </div>

                {/* Right Column (25% - Timer + Status) */}
                <div className="w-full lg:w-1/4 flex flex-col gap-4">

                    {/* Timer */}
                    <div className={`rounded-xl p-4 text-center shadow-md border 
                        ${timeLeft <= 10 && !isAnsweredByStudent
                            ? 'bg-red-100 text-red-700 border-red-500'
                            : 'bg-gray-100 text-gray-800 border-gray-300'}`}>
                        <p className="text-lg font-semibold text-gray-600">Time Left</p>
                        <p className="text-4xl font-extrabold text-gray-800 mt-1">
                            {String(timeLeft).padStart(2, '0')}s
                        </p>
                    </div>

                    {/* Status/Feedback */}
                    <div className='justify-between'>
                        {isAnsweredByStudent ? (
                            <p className="font-bold text-gray-800">✅ Answer Submitted!</p>
                        ) : (
                            inputIsDisabled && timeLeft === 0 ? (
                                <p className="font-bold text-red-600">❌ Time Expired!</p>
                            ) : (
                                <p className="font-medium text-gray-500">Select an option below.</p>
                            )
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

// --- StudentResultsScreen ---
// ... (StudentResultsScreen remains the same, relying on fullQuestionState)

const StudentResultsScreen = ({ fullQuestionState, liveState, studentId }) => {
    // fullQuestionState is the merged array
    const questionsArray = fullQuestionState;
    const joinedStudents = liveState.joinedStudents;
    const student = joinedStudents.find(s => s.id === studentId);
    let correctCount = 0;

    const results = questionsArray.map((q) => {
        // The properties (fullQuestionText, correctAnswer) need to be derived in the result calculation
        const studentAnswer = q.answeredBy[studentId];
        const correctAnswer = getCorrectAnswerText(q);
        const fullQuestionText = getFullQuestionText(q);

        const isCorrect = studentAnswer === correctAnswer;
        if (isCorrect) correctCount++;
        return { q: { ...q, fullQuestionText, correctAnswer }, studentAnswer, isCorrect };
    });

    const studentScores = joinedStudents.map(s => {
        let score = 0;
        questionsArray?.forEach(q => {
            const correctAnswer = getCorrectAnswerText(q);
            if (q.answeredBy[s.id] === correctAnswer) {
                score++;
            }
        });
        return { id: s.id, name: s.name, score };
    }).sort((a, b) => b.score - a.score);

    const rank = studentScores.findIndex(s => s.id === studentId) + 1;

    return (
        <div className="flex flex-col items-center w-full space-y-8 animate-fade-in">
            <h1 className="text-4xl font-extrabold text-indigo-600 text-center">Your Quiz Results</h1>
            <h2 className="text-2xl font-bold text-gray-600">{student?.name}</h2>

            <div className="bg-indigo-100 p-6 rounded-2xl shadow-lg mt-8 w-full max-w-sm text-center border-4 border-indigo-300">
                <p className="text-xl font-bold text-indigo-800">Final Score: {correctCount} / {questionsArray.length}</p>
                <p className="text-lg font-medium text-indigo-600">Your Rank: **{rank}** out of {joinedStudents.length} participants</p>
            </div>

            <div className="w-full max-w-2xl space-y-4">
                {results.map(({ q, studentAnswer, isCorrect }, index) => {
                    const questionParts = q.fullQuestionText.split('\n\n');
                    const mainQuestion = questionParts[0];

                    return (
                        <div key={q.id} className={`rounded-2xl p-6 shadow-md transition-all duration-300 ${isCorrect ? 'bg-green-50 border-2 border-green-300' : 'bg-rose-50 border-2 border-rose-300'}`}>
                            <h3 className="text-lg font-bold text-gray-700 mb-2">Question {index + 1}: {mainQuestion}</h3>
                            <div className="mt-2 text-base">
                                <p className="mb-1">Your Answer: <span className={`font-bold ${isCorrect ? 'text-green-600' : 'text-rose-600'}`}>{studentAnswer || "Not Answered"}</span></p>
                                <p>Correct Answer: <span className="font-bold text-green-600">{q.correctAnswer}</span></p>
                            </div>
                        </div>
                    );
                })}
            </div>
            <p
                // onClick={onRestart}
                className="px-8 py-4 text-xl font-bold rounded-full transition-all duration-300 bg-rose-600 text-white shadow-lg hover:bg-rose-500 hover:scale-105"
            >
                Session ends here!
            </p>
        </div>
    );
};